import React from 'react';
import { UnscheduledEvent } from '../../types';
import {
  ERTS_REMOVED_CONFIDENCE,
  EVENT_STATUS,
  ERTS_INITIAL_CHANGE_REASON,
  ERTS_FINAL_CHANGE_REASON,
  ERTS_FINAL_CONFIDENCE,
} from '../../constants';
import {
  extractDate,
  extractTime,
  getErtsTimeDisplay,
  isoDatePart,
  composeZuluDateTime,
  isDateTimeIncomplete,
} from './dateTimeUtils';
import { validateErts, validateNextAdvise, TimeRuleContext } from './timeValidation';
import { sortErtsHistoryForDisplay } from './saveHelpers';

/**
 * ERTS history entry type.
 */
export interface ErtsHistoryEntry {
  ertsDate: string;
  ertsTime: string;
  confidence: string;
  changeReason: string;
  submittedBy: string;
  submittedAt: string;
}

/**
 * Props for the ErtsSection component.
 */
interface ErtsSectionProps {
  formData: UnscheduledEvent;
  isReadOnly: boolean;
  updateField: (field: keyof UnscheduledEvent, value: string | number | boolean) => void;
  ertsEdited: boolean;
  setErtsEdited: React.Dispatch<React.SetStateAction<boolean>>;
  ertsConfidence: string;
  setErtsConfidence: React.Dispatch<React.SetStateAction<string>>;
  ertsChangeReason: string;
  setErtsChangeReason: React.Dispatch<React.SetStateAction<string>>;
  ertsChangeReasonOther: string;
  setErtsChangeReasonOther: React.Dispatch<React.SetStateAction<string>>;
  ertsHistory: ErtsHistoryEntry[];
  isErtsHistoryOpen: boolean;
  setIsErtsHistoryOpen: React.Dispatch<React.SetStateAction<boolean>>;
  /** Whether this record had an ERTS when the modal opened (drives removal UX). */
  ertsExisted: boolean;
  /** Whether Next Advise is a required field for this record (gates Create). */
  nextAdviseRequired: boolean;
  /** Whether ERTS is a required field for this record (Scheduled). */
  ertsRequired?: boolean;
  /** When creating, disables Next Advise until Time Declared is entered. */
  awaitingTimeDeclared?: boolean;
  /** Next Advise value when the modal opened — gates the 90-minute past
   * advisory so it only appears when the field is actually changed. */
  originalNextAdvise?: string;
}

/**
 * ERTS date/time inputs, confidence, change reason, initial ERTS, next advise, and ERTS history table.
 */
const ErtsSection: React.FC<ErtsSectionProps> = ({
  formData,
  isReadOnly,
  updateField,
  ertsEdited,
  setErtsEdited,
  ertsConfidence,
  setErtsConfidence,
  ertsChangeReason,
  setErtsChangeReason,
  ertsChangeReasonOther,
  setErtsChangeReasonOther,
  ertsHistory,
  isErtsHistoryOpen,
  setIsErtsHistoryOpen,
  ertsExisted,
  nextAdviseRequired,
  ertsRequired,
  awaitingTimeDeclared,
  originalNextAdvise,
}) => {
  // True while the current ERTS is the initial, not-yet-committed submission
  // (no history rows recorded yet). While initial, the Initial ERTS field tracks
  // edits to the ERTS until the record is saved.
  const isInitialErts = ertsHistory.length === 0;

  const handleErtsDateChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    // Preserve any already-entered (possibly partial) time and recompose so the
    // date and time round-trip cleanly regardless of entry order.
    const time = getErtsTimeDisplay(formData.returnToServiceTimeZulu);
    const newVal = composeZuluDateTime(e.target.value, time);
    updateField('returnToServiceTimeZulu', newVal);
    setErtsEdited(true);
    if (isInitialErts && !ertsChangeReason) {
      setErtsChangeReason(ERTS_INITIAL_CHANGE_REASON);
      setErtsConfidence('');
    }
    // While still the initial (uncommitted) ERTS, keep Initial ERTS synced to
    // every edit so it reflects the final value at the time of save.
    if (isInitialErts) {
      updateField('initialReturnToServiceTimeZulu', newVal);
    }
    // On a Completed record, keep Next Advise matched to the ERTS so it can
    // never sit after the ERTS (which would block the update).
    if (formData.status === EVENT_STATUS.COMPLETED) {
      updateField('nextAdviseTimeZulu', newVal);
    }
  };

  const handleErtsTimeChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    let val = e.target.value.replace(/[^0-9]/g, '');
    if (val.length > 4) val = val.substring(0, 4);
    const formatted = val.length >= 3 ? val.substring(0, 2) + ':' + val.substring(2) : val;
    // Keep the existing date (or empty) and recompose. Entering a time before a
    // date no longer corrupts the date input or drops the typed time.
    const newVal = composeZuluDateTime(isoDatePart(formData.returnToServiceTimeZulu), formatted);
    updateField('returnToServiceTimeZulu', newVal);
    setErtsEdited(true);
    if (isInitialErts && !ertsChangeReason) {
      setErtsChangeReason(ERTS_INITIAL_CHANGE_REASON);
      setErtsConfidence('');
    }
    // While still the initial (uncommitted) ERTS, keep Initial ERTS synced.
    if (isInitialErts) {
      updateField('initialReturnToServiceTimeZulu', newVal);
    }
    // On a Completed record, keep Next Advise matched to the ERTS so it can
    // never sit after the ERTS (which would block the update).
    if (formData.status === EVENT_STATUS.COMPLETED) {
      updateField('nextAdviseTimeZulu', newVal);
    }
  };

  // Updates the ERTS change reason. Selecting "Final ERTS" auto-populates the
  // Confidence field to "Final" (a value that is never offered as a manually
  // selectable option); switching away from "Final ERTS" clears that
  // auto-populated confidence so the user must choose one explicitly.
  const handleChangeReasonChange = (event: React.ChangeEvent<HTMLSelectElement>): void => {
    const reason = event.target.value;
    setErtsChangeReason(reason);
    if (reason !== 'Other') setErtsChangeReasonOther('');
    if (reason === ERTS_FINAL_CHANGE_REASON) {
      setErtsConfidence(ERTS_FINAL_CONFIDENCE);
    } else if (ertsConfidence === ERTS_FINAL_CONFIDENCE) {
      setErtsConfidence('');
    }
  };

  // Clears the ERTS date and time in one action — a cleaner removal than
  // emptying the native date segments. When a previously-existing ERTS is
  // removed, the confidence becomes "Removed ERTS" and a change reason is
  // required; the removal is recorded in ERTS History on save.
  const handleClearErts = (): void => {
    updateField('returnToServiceTimeZulu', '');
    if (ertsExisted) {
      // Removing a previously-committed ERTS: record the removal — confidence
      // becomes "Removed ERTS", a change reason is required, and the removal is
      // logged to ERTS History on save.
      setErtsEdited(true);
      setErtsConfidence(ERTS_REMOVED_CONFIDENCE);
      setErtsChangeReason('');
    } else {
      // A fresh, uncommitted ERTS entered in error and cleared before saving:
      // fully back it out so the record returns to its original no-ERTS state.
      // Clearing the edited flag hides the Change Reason/Confidence fields and
      // drops their required status, so they no longer block Create/Update.
      updateField('initialReturnToServiceTimeZulu', '');
      setErtsEdited(false);
      setErtsConfidence('');
      setErtsChangeReason('');
      setErtsChangeReasonOther('');
    }
  };

  // True when a previously-existing ERTS has been fully removed this session.
  const ertsRemoved = ertsEdited && ertsExisted && !formData.returnToServiceTimeZulu;

  // Next Advise is required (and currently incomplete) for new full-detail
  // records — highlight it red until a valid date+time is entered.
  const nextAdviseMissing =
    nextAdviseRequired &&
    !isReadOnly &&
    !awaitingTimeDeclared &&
    isDateTimeIncomplete(formData.nextAdviseTimeZulu);

  // ERTS is required (and currently incomplete) for Scheduled records and,
  // because completing an event finalizes its ERTS, for any Completed record.
  // (This section only renders for non-Deferred records.)
  const isCompleted = formData.status === EVENT_STATUS.COMPLETED;
  const ertsMissing =
    !isReadOnly &&
    (!!ertsRequired || isCompleted) &&
    isDateTimeIncomplete(formData.returnToServiceTimeZulu);

  // Cross-field temporal rules: ERTS and Next Advise relative to Time Declared,
  // the 90-minute past limit, and the ERTS ceiling once the event is Completed.
  const timeRuleContext: TimeRuleContext = {
    timeDeclared: formData.timeDeclaredZulu,
    erts: formData.returnToServiceTimeZulu,
    isCompleted,
    completedByAliasLogged: !!formData.completedByAlias,
  };
  const ertsRuleError = isReadOnly
    ? null
    : validateErts(formData.returnToServiceTimeZulu, timeRuleContext);
  const nextAdviseRuleError = isReadOnly
    ? null
    : validateNextAdvise(formData.nextAdviseTimeZulu, timeRuleContext, {
        enforcePastLimit: formData.nextAdviseTimeZulu !== originalNextAdvise,
      });

  const handleNextAdviseDate = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const time = getErtsTimeDisplay(formData.nextAdviseTimeZulu);
    updateField('nextAdviseTimeZulu', composeZuluDateTime(e.target.value, time));
  };

  const handleNextAdviseTime = (e: React.ChangeEvent<HTMLInputElement>): void => {
    let val = e.target.value.replace(/[^0-9]/g, '');
    if (val.length > 4) val = val.substring(0, 4);
    const formatted = val.length >= 3 ? val.substring(0, 2) + ':' + val.substring(2) : val;
    updateField(
      'nextAdviseTimeZulu',
      composeZuluDateTime(isoDatePart(formData.nextAdviseTimeZulu), formatted),
    );
  };

  // Most recent ERTS submission, used to display the current Confidence/Reason
  // when not actively editing. Falls back to the values stored on the event.
  const latestErts = sortErtsHistoryForDisplay(ertsHistory)[0];
  const latestConfidence = latestErts?.confidence || formData.returnToServiceConfidence || '';
  const latestReason = latestErts?.changeReason || formData.returnToServiceChangeReason || '';
  const showLatestErts =
    !(ertsEdited && !isReadOnly) && (latestConfidence !== '' || latestReason !== '');

  return (
    <>
      {/* ERTS + ERTS Details */}
      <div className="field-row erts-edit-row">
        <label className="field-label">
          ERTS
          {formData.revisionCounter > 0 && (
            <span className="revision-badge">{formData.revisionCounter}</span>
          )}
        </label>
        <div className="field-group field-group-compact">
          <div className="field-input-block field-input-half">
            <span className="field-input-label">Date</span>
            <input
              type="date"
              value={isoDatePart(formData.returnToServiceTimeZulu)}
              readOnly={isReadOnly}
              disabled={isReadOnly}
              onChange={handleErtsDateChange}
              className={`field-input${ertsMissing || ertsRuleError ? ' field-required-missing' : ''}`}
            />
          </div>
          <div className="field-input-block field-input-half">
            <span className="field-input-label">Zulu Time</span>
            <input
              type="text"
              value={getErtsTimeDisplay(formData.returnToServiceTimeZulu)}
              readOnly={isReadOnly}
              onChange={handleErtsTimeChange}
              placeholder="HH:MM"
              maxLength={5}
              className={`field-input${ertsMissing || ertsRuleError ? ' field-required-missing' : ''}`}
            />
          </div>
          {!isReadOnly && formData.returnToServiceTimeZulu && (
            <button
              type="button"
              className="erts-clear-btn"
              onClick={handleClearErts}
              aria-label="Remove ERTS"
              title="Remove ERTS"
            >
              &times;
            </button>
          )}
        </div>
        {ertsEdited && !isReadOnly && (
          <>
            <div className="field-input-block erts-change-reason-block">
              <span className="field-input-label">Change Reason *</span>
              {ertsChangeReason === ERTS_INITIAL_CHANGE_REASON ? (
                <input
                  type="text"
                  className="field-input"
                  value={ERTS_INITIAL_CHANGE_REASON}
                  readOnly
                />
              ) : (
                <select
                  className={`field-input${!ertsChangeReason ? ' field-required-missing' : ''}`}
                  value={ertsChangeReason}
                  onChange={handleChangeReasonChange}
                >
                  <option value="">Select Reason...</option>
                  <option value="Parts ETA Change">Parts ETA Change</option>
                  <option value="Additional Parts Required">Additional Parts Required</option>
                  <option value="Additional Mx Findings">Additional Mx Findings</option>
                  <option value="Additional Mx Time Required">Additional Mx Time Required</option>
                  <option value="Tooling">Tooling</option>
                  <option value="Equipment">Equipment</option>
                  <option value="Dark Hours">Dark Hours</option>
                  <option value="ERTS Correction from Carrier">ERTS Correction from Carrier</option>
                  <option value={ERTS_FINAL_CHANGE_REASON}>Final ERTS</option>
                  <option value="Other">Other (manual entry)</option>
                </select>
              )}
            </div>
            <div className="field-input-block field-input-half">
              <span className="field-input-label">Confidence *</span>
              <select
                className={`field-input${!ertsConfidence ? ' field-required-missing' : ''}`}
                value={ertsConfidence}
                onChange={(e) => setErtsConfidence(e.target.value)}
              >
                {ertsRemoved && <option value={ERTS_REMOVED_CONFIDENCE}>Removed ERTS</option>}
                {ertsConfidence === ERTS_FINAL_CONFIDENCE && (
                  <option value={ERTS_FINAL_CONFIDENCE}>Final</option>
                )}
                <option value="">Select...</option>
                <option value="Low">Low</option>
                <option value="Medium">Medium</option>
                <option value="High">High</option>
              </select>
            </div>
            {ertsChangeReason === 'Other' && (
              <div className="field-input-block">
                <span className="field-input-label">Other Reason</span>
                <input
                  type="text"
                  className="field-input"
                  value={ertsChangeReasonOther}
                  onChange={(e) => setErtsChangeReasonOther(e.target.value)}
                  placeholder="Enter reason"
                />
              </div>
            )}
          </>
        )}
        {showLatestErts && (
          <>
            <div className="field-input-block erts-change-reason-block">
              <span className="field-input-label">Change Reason</span>
              <input
                type="text"
                className="field-input"
                value={latestReason || '—'}
                readOnly
                disabled
              />
            </div>
            <div className="field-input-block field-input-half">
              <span className="field-input-label">Confidence</span>
              <input
                type="text"
                className="field-input"
                value={latestConfidence || '—'}
                readOnly
                disabled
              />
            </div>
          </>
        )}
        {ertsRuleError && (
          <span className="field-inline-error" role="alert">
            {ertsRuleError}
          </span>
        )}
      </div>

      {/* Initial ERTS + Next Advise */}
      <div className="field-row">
        <label className="field-label">Initial ERTS</label>
        <div className="field-group field-group-compact">
          <div className="field-input-block field-input-half">
            <span className="field-input-label">Date</span>
            <input
              type="text"
              value={extractDate(formData.initialReturnToServiceTimeZulu)}
              readOnly
              className="field-input"
            />
          </div>
          <div className="field-input-block field-input-half">
            <span className="field-input-label">Zulu Time</span>
            <input
              type="text"
              value={extractTime(formData.initialReturnToServiceTimeZulu)}
              readOnly
              className="field-input"
            />
          </div>
        </div>
        <label className="field-label">Next Advise</label>
        <div className="field-group field-group-compact">
          <div className="field-input-block field-input-half">
            <span className="field-input-label">Date</span>
            <input
              type="date"
              value={isoDatePart(formData.nextAdviseTimeZulu)}
              readOnly={isReadOnly}
              disabled={isReadOnly || awaitingTimeDeclared}
              onChange={handleNextAdviseDate}
              className={`field-input${nextAdviseMissing || nextAdviseRuleError ? ' field-required-missing' : ''}`}
            />
          </div>
          <div className="field-input-block field-input-half">
            <span className="field-input-label">Zulu Time</span>
            <input
              type="text"
              value={getErtsTimeDisplay(formData.nextAdviseTimeZulu)}
              readOnly={isReadOnly}
              disabled={awaitingTimeDeclared}
              onChange={handleNextAdviseTime}
              placeholder="HH:MM"
              maxLength={5}
              className={`field-input${nextAdviseMissing || nextAdviseRuleError ? ' field-required-missing' : ''}`}
            />
          </div>
          {nextAdviseRuleError && (
            <span className="field-inline-error" role="alert">
              {nextAdviseRuleError}
            </span>
          )}
        </div>
      </div>

      {/* ERTS History Collapsible */}
      <div className="collapsible-section">
        <button
          className="collapsible-toggle"
          type="button"
          onClick={() => setIsErtsHistoryOpen(!isErtsHistoryOpen)}
        >
          <span className={`toggle-arrow ${isErtsHistoryOpen ? 'open' : ''}`}>&#9656;</span>
          ERTS History
          {ertsHistory.length > 0 && <span className="revision-badge">{ertsHistory.length}</span>}
        </button>
        {isErtsHistoryOpen && (
          <div className="collapsible-content">
            {ertsHistory.length === 0 ? (
              <div className="barriers-empty">No ERTS submissions recorded</div>
            ) : (
              <div className="erts-history-table">
                <div className="erts-history-header">
                  <span>ERTS Date</span>
                  <span>Zulu Time</span>
                  <span>Confidence</span>
                  <span>Change Reason</span>
                  <span>Submitted By</span>
                  <span>Submitted At</span>
                </div>
                {sortErtsHistoryForDisplay(ertsHistory).map((entry, index) => (
                  <div key={index} className="erts-history-row">
                    <span>{entry.ertsDate}</span>
                    <span>{entry.ertsTime}</span>
                    <span>{entry.confidence}</span>
                    <span>{entry.changeReason}</span>
                    <span>{entry.submittedBy}</span>
                    <span>{entry.submittedAt}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
};

export default ErtsSection;
